import { Field } from '@base-ui/react';
import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { autofillSurface, changedField, fieldBorder, focusRing } from '../../interaction-states';
import { scrollbarStyles } from '../../scrollbar';
import { typographyVariantStyles } from '../../typography-variants';
import type { HelperState } from '../Helper/types';

function getBorderColor(theme: Theme, state: HelperState): string {
  switch (state) {
    case 'destructive':
      return theme.colors.error.border;
    case 'success':
      return theme.colors.success.border;
    default:
      return theme.colors.border.default;
  }
}

export const StyledRoot = styled(Field.Root)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: '100%',

  '&[data-disabled]': {
    opacity: 0.5,
    pointerEvents: 'none',
  },

  ...changedField(theme),
}));

export const StyledBox = styled('div', {
  shouldForwardProp: (prop) => prop !== '$state',
})<{ $state: HelperState }>(({ theme, $state }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
  width: '100%',
  minHeight: 60,
  boxSizing: 'border-box',
  borderRadius: theme.radius[8],
  backgroundColor: theme.colors.background.overlay,
  border: `1px solid ${fieldBorder(getBorderColor(theme, $state))}`,
  boxShadow: theme.shadows.sm,
  transition: 'box-shadow 0.15s ease',

  ...focusRing(theme, { within: true }),
}));

export const StyledTextarea = styled('textarea')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: 48,
  maxHeight: 240,
  border: 'none',
  outline: 'none',
  padding: '6px 14px',
  background: 'transparent',
  resize: 'vertical',
  overflowY: 'auto',
  overflowX: 'hidden',
  overflowWrap: 'anywhere',
  ...scrollbarStyles,
  color: theme.colors.text.primary,
  fontFamily: 'inherit',
  ...typographyVariantStyles.body3,

  '&::placeholder': {
    color: theme.colors.text.secondary,
  },

  '&:disabled': {
    cursor: 'not-allowed',
  },

  '&::-webkit-resizer': {
    display: 'none',
  },

  ...autofillSurface(theme),
}));

export const StyledResizeHandle = styled('span')(({ theme }) => ({
  position: 'absolute',
  right: 1,
  bottom: 1,
  width: 16,
  height: 16,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.icon.secondary,
  pointerEvents: 'none',

  '& svg': {
    width: '100%',
    height: '100%',
  },
}));
